import { useState, useEffect } from 'react';
import { getAuditLog } from '../api';
import { useMobile } from '../components/Layout';
import { useTheme } from '../ThemeContext';
import { Search, Download, Filter, RefreshCw } from 'lucide-react';

const MODULE_META = {
  PLATFORM: { label: 'Platform',   bg: '#EFF6FF', text: '#1D4ED8' },
  MODULE_1: { label: 'Suitability', bg: '#F0FDF4', text: '#166534' },
  MODULE_2: { label: 'Documents',  bg: '#FFFBEB', text: '#92400E' },
  MODULE_3: { label: 'KYC/AML',   bg: '#F5F3FF', text: '#6B21A8' },
  MODULE_4: { label: 'Regulatory', bg: '#CCFBF1', text: '#134E4A' },
  MODULE_5: { label: 'Comms',      bg: '#FCE7F3', text: '#9D174D' },
  MODULE_6: { label: 'Audit/DORA', bg: '#FEE2E2', text: '#991B1B' },
};

const getPresetDates = (preset) => {
  const now = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  switch (preset) {
    case '7d':   return { from: fmt(new Date(now - 7  * 86400000)), to: fmt(now) };
    case '30d':  return { from: fmt(new Date(now - 30 * 86400000)), to: fmt(now) };
    case '90d':  return { from: fmt(new Date(now - 90 * 86400000)), to: fmt(now) };
    case 'year': return { from: fmt(new Date(now.getFullYear(), 0, 1)), to: fmt(now) };
    default:     return { from: '', to: '' };
  }
};

const AuditLog = () => {
  const isMobile      = useMobile();
  const { theme }     = useTheme();

  const [records,     setRecords]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [module,      setModule]      = useState('');
  const [search,      setSearch]      = useState('');
  const [dateFrom,    setDateFrom]    = useState('');
  const [dateTo,      setDateTo]      = useState('');
  const [entityId,    setEntityId]    = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [preset,      setPreset]      = useState('30d');

  const exportReport = (format) => {
    const base   = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/reports/audit-log`;
    const params = new URLSearchParams();
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo)   params.append('date_to',   dateTo);
    if (module)   params.append('module',    module);
    if (entityId) params.append('entity_id', entityId);
    const token = localStorage.getItem('token');
    if (token) params.append('token', token);
    window.open(`${base}/${format}?${params.toString()}`, '_blank');
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (module)   params.module    = module;
      if (entityId) params.entity_id = entityId;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
      const res = await getAuditLog(params);
      setRecords(res.data.records || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    const { from, to } = getPresetDates('30d');
    setDateFrom(from); setDateTo(to);
  }, []);

  useEffect(() => {
    if (dateFrom !== undefined) fetchLogs();
  }, [dateFrom, dateTo, module, entityId]);

  const handlePreset = (p) => {
    setPreset(p);
    const { from, to } = getPresetDates(p);
    setDateFrom(from); setDateTo(to);
  };

  const filtered = records.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return r.action.toLowerCase().includes(q) || r.actor.toLowerCase().includes(q);
  });

  const inputStyle = {
    padding: '8px 12px', borderRadius: 8,
    border: `1px solid ${theme.border}`, fontSize: 13,
    color: theme.textPrimary, background: theme.bgInput,
    outline: 'none', boxSizing: 'border-box', width: '100%',
  };

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: theme.textPrimary }}>
            Immutable Audit Log
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.textSecondary }}>
            Cryptographic hash chain · tamper-evident compliance trail
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: 10, padding: '10px 18px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.textPrimary, lineHeight: 1 }}>{records.length}</p>
            <p style={{ margin: '2px 0 0', fontSize: 9, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Records</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ background: theme.bgCard, borderRadius: 10, padding: '12px 16px', marginBottom: 14, border: `1px solid ${theme.border}` }}>
        {/* Top row */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
            <Search size={13} color={theme.textTertiary} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input type="text" placeholder="Search by action or actor…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ ...inputStyle, paddingLeft: 30 }} />
          </div>

          {isMobile && (
            <button onClick={() => setShowFilters(v => !v)} style={{
              padding: '8px 12px', borderRadius: 8,
              background: showFilters ? '#3B82F6' : theme.bgBadge,
              color: showFilters ? 'white' : theme.textSecondary,
              border: `1px solid ${showFilters ? '#3B82F6' : theme.border}`,
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              <Filter size={13} /> Filters
            </button>
          )}

          <button onClick={fetchLogs} style={{
            padding: '8px 14px', borderRadius: 8, background: '#3B82F6',
            color: 'white', border: 'none', fontSize: 12, fontWeight: 700,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <RefreshCw size={13} /> Apply
          </button>

          <button onClick={() => exportReport('csv')} style={{
            padding: '8px 12px', borderRadius: 8,
            background: theme.bgBadge, color: theme.textSecondary,
            border: `1px solid ${theme.border}`, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <Download size={13} /> CSV
          </button>

          <button onClick={() => exportReport('pdf')} style={{
            padding: '8px 12px', borderRadius: 8,
            background: theme.bgBadge, color: theme.textSecondary,
            border: `1px solid ${theme.border}`, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <Download size={13} /> PDF
          </button>
        </div>

        {/* Advanced filters */}
        {(!isMobile || showFilters) && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 8, marginTop: 10 }}>
            <select value={module} onChange={e => setModule(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">All Modules</option>
              {Object.entries(MODULE_META).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>

            <select value={preset} onChange={e => handlePreset(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="year">This year</option>
              <option value="all">All time</option>
              <option value="custom">Custom range</option>
            </select>

            {preset === 'custom' ? (
              <>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputStyle} />
                <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={inputStyle} />
              </>
            ) : (
              <input type="text" placeholder="Entity ID / pseudo_id…" value={entityId}
                onChange={e => setEntityId(e.target.value)} style={inputStyle} />
            )}
          </div>
        )}
      </div>

      {/* Records */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14 }}>
          Loading audit records…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.textTertiary, fontSize: 14, background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}` }}>
          No records found.
        </div>
      ) : isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(record => {
            const mod = MODULE_META[record.module];
            return (
              <div key={record.record_id} style={{ background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}`, padding: '12px 14px', boxShadow: theme.shadowSm }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, gap: 8 }}>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: theme.textPrimary, flex: 1 }}>
                    {record.action.replace(/_/g, ' ')}
                  </p>
                  <span style={{ background: mod?.bg || theme.bgBadge, color: mod?.text || theme.textSecondary, padding: '2px 9px', borderRadius: 99, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>
                    {mod?.label || record.module}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: theme.textSecondary }}>{record.actor}</span>
                  <span style={{ fontSize: 11, color: theme.textTertiary }}>
                    {new Date(record.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  {record.entity_id && (
                    <code style={{ background: '#EFF6FF', color: '#1D4ED8', padding: '1px 7px', borderRadius: 5, fontSize: 10, fontWeight: 600 }}>
                      {record.entity_id}
                    </code>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ background: theme.bgCard, borderRadius: 10, border: `1px solid ${theme.border}`, boxShadow: theme.shadowSm, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: theme.bgBase, borderBottom: `1px solid ${theme.border}` }}>
                {['Timestamp', 'Module', 'Event', 'Actor', 'Entity', 'Action'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: theme.textTertiary, letterSpacing: '0.07em', textTransform: 'uppercase' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((record, idx) => {
                const mod = MODULE_META[record.module];
                return (
                  <tr key={record.record_id} style={{ borderBottom: idx < filtered.length - 1 ? `1px solid ${theme.borderLight}` : 'none', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = theme.bgCardHover}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 16px', fontSize: 12, color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                      {new Date(record.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '11px 16px' }}>
                      <span style={{ background: mod?.bg || theme.bgBadge, color: mod?.text || theme.textSecondary, padding: '2px 9px', borderRadius: 99, fontSize: 11, fontWeight: 700 }}>
                        {mod?.label || record.module}
                      </span>
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 12, color: theme.textTertiary }}>{record.event_type}</td>
                    <td style={{ padding: '11px 16px', fontSize: 12, color: theme.textSecondary, fontWeight: 500 }}>{record.actor}</td>
                    <td style={{ padding: '11px 16px' }}>
                      {record.entity_id ? (
                        <code style={{ background: record.entity_id.startsWith('INV-') ? '#EFF6FF' : theme.bgBadge, color: record.entity_id.startsWith('INV-') ? '#1D4ED8' : theme.textSecondary, padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600 }}>
                          {record.entity_id}
                        </code>
                      ) : <span style={{ fontSize: 11, color: theme.textTertiary }}>—</span>}
                    </td>
                    <td style={{ padding: '11px 16px', fontSize: 13, color: theme.textPrimary, fontWeight: 500 }}>
                      {record.action.replace(/_/g, ' ')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ color: theme.textTertiary, fontSize: 12, marginTop: 10, textAlign: 'center' }}>
        Showing {filtered.length} of {records.length} records
        {preset !== 'all' && (
          <button onClick={() => handlePreset('all')} style={{ marginLeft: 8, background: 'none', border: 'none', color: '#3B82F6', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
            Show all time
          </button>
        )}
      </p>
    </div>
  );
};

export default AuditLog;